import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  inject
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedModule } from '@shared/shared.module';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SIDENAV_DATA } from '@shared/services/sidenav/sidenav.data';
import { SidenavService } from '@shared/services/sidenav/sidenav.service';
import { FileSignatureController } from '@shared/controllers/file-signature.controller';
import { RemoveModalController } from '@shared/controllers/remove-modal.controller';
import { ProjectSiteDiaryEntry } from '@modules/projects/shared/interfaces/project-site-diary.interface';
import {
  getEntryTypeConfig,
  ProjectSiteDiaryEntryTypeConfig
} from '@modules/projects/modules/project-site-diary/project-site-diary.helper';
import { ProjectSiteDiaryDetailService } from '../project-site-diary-detail/project-site-diary-detail.service';
import { isItemNotFoundError } from './project-site-diary-entry.helper';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import {
  mapProjectSiteDiaryMedia,
  ProjectSiteDiaryMediaView
} from '@modules/projects/shared/helpers/project-site-diary-media.helper';
import { RICH_MEDIA_QUILL_FORMATS, RICH_MEDIA_QUILL_TOOLBAR } from '@shared/consts/rich-media-quill.const';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { AttachmentService } from '@shared/services/attachment.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { AttachmentsManageService } from '@shared/modules/attachment-manage/services/attachments-manage.service';
import { Task } from '@shared/models/task.model';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { MatDialog } from '@angular/material/dialog';
import {
  AttachmentLibraryDialogResult,
  AttachmentLibraryModalComponent
} from '@shared/modules/attachment-manage/components/attachment-library-modal/attachment-library-modal.component';

@Component({
  selector: 'project-site-diary-entry-sidenav',
  standalone: true,
  imports: [TranslateModule, SharedModule, MatProgressSpinnerModule],
  templateUrl: './project-site-diary-entry-sidenav.component.html',
  styleUrls: ['./project-site-diary-entry-sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AttachmentsManageService]
})
export class ProjectSiteDiaryEntrySidenavComponent implements OnInit, OnDestroy {
  static readonly componentName = 'ProjectSiteDiaryEntrySidenavComponent';
  protected readonly ButtonTypes = ButtonTypes;
  protected readonly ButtonSize = ButtonSize;

  private readonly service = inject(ProjectSiteDiaryDetailService);
  private readonly sidenav = inject(SidenavService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly signatureController = inject(FileSignatureController);
  private readonly translate = inject(TranslateService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly attachmentsManageService = inject(AttachmentsManageService);
  private readonly snackBar = inject(SnackBarService);
  private readonly listTaskService = inject(ListTaskService);
  private readonly taskService = inject(TaskService);
  private readonly dialog = inject(MatDialog);
  protected readonly taskSidenavService = inject(TaskSidenavService);

  protected entry: ProjectSiteDiaryEntry | null = null;
  protected media: ProjectSiteDiaryMediaView[] = [];
  protected loading = false;
  /** The entry has been removed (e.g. it is opened from the logbook) */
  protected entryDeleted = false;
  protected loadFailed = false;
  protected deleting = false;
  protected uploadingMedia = false;
  protected readonly deletingMediaIds = new Set<number>();
  protected savingDescription = false;
  protected editingDescription = false;
  protected readonly descriptionControl = new UntypedFormControl('');
  protected readonly quillFormats = RICH_MEDIA_QUILL_FORMATS;
  protected readonly descriptionModules = { toolbar: RICH_MEDIA_QUILL_TOOLBAR };
  protected typeConfig: ProjectSiteDiaryEntryTypeConfig = getEntryTypeConfig('note');
  protected typeLabel = '';

  constructor(@Inject(SIDENAV_DATA) private readonly sideData: SIDENAV_DATA) {}

  private get entryId(): number {
    return Number(this.sideData?.data?.entryId);
  }

  private get controlTaskId(): number {
    return Number(this.sideData?.data?.taskId);
  }

  private get controlTask(): Task | null {
    return (this.sideData?.data?.task as Task) || null;
  }

  private get projectId(): number {
    return Number(this.sideData?.data?.projectId || this.controlTask?.project?.id);
  }

  protected get isControlDocumentation(): boolean {
    return this.sideData?.data?.mode === 'control' && this.controlTaskId > 0;
  }

  protected get canDeleteDocumentation(): boolean {
    return !!this.entry?.id && !this.deleting;
  }

  protected get controlDocumentationTitle(): string {
    return this.entry?.title || this.controlTask?.description || this.typeLabel;
  }

  ngOnInit(): void {
    this.loadEntry();
  }

  ngOnDestroy(): void {
    this.taskSidenavService.resetExpandedWidth();
  }

  protected close(): void {
    this.sidenav.close();
  }

  protected delete(): void {
    if (!this.entry?.id || this.deleting) return;

    new RemoveModalController().remove(() => this.deleteEntry()).subscribe();
  }

  protected toggleWidth(): void {
    this.taskSidenavService.toggleExpandedWidth();
  }

  protected startDescriptionEdit(): void {
    if (!this.entry || this.savingDescription) return;

    this.editingDescription = true;
    this.descriptionControl.setValue(this.entry.description || '');
    this.descriptionControl.markAsPristine();
    this.changeDetector.markForCheck();

    requestAnimationFrame(() => {
      const editor = this.elementRef.nativeElement.querySelector(
        '.site-diary-entry-sidenav__description-editor .ql-editor'
      );
      editor?.focus();
    });
  }

  protected cancelDescriptionEdit(): void {
    if (!this.entry) return;

    this.descriptionControl.setValue(this.entry.description || '');
    this.descriptionControl.markAsPristine();
    this.editingDescription = false;
  }

  protected saveDescriptionEdit(): void {
    if (!this.entry || this.savingDescription || !this.descriptionControl.dirty) return;

    this.savingDescription = true;
    const description = this.descriptionControl.value || '';
    const request$ = this.isControlDocumentation
      ? this.service.updateTaskDocumentation(this.controlTaskId, { description: description || null })
      : this.service.updateEntryDescription(this.entry.id, description);

    request$.subscribe({
      next: (updatedEntry) => {
        this.entry = {
          ...this.entry,
          ...updatedEntry,
          description: this.isControlDocumentation ? (updatedEntry.description ?? null) : description
        };
        this.descriptionControl.markAsPristine();
        this.editingDescription = false;
        this.savingDescription = false;
        this.service.entryChanged.emit({ action: 'update', id: this.entry.id });
        if (this.isControlDocumentation) this.refreshControlTask();
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.savingDescription = false;
        this.changeDetector.markForCheck();
      }
    });
  }

  protected isImage(media: ProjectSiteDiaryMediaView): boolean {
    return media.isImageFile;
  }

  protected isVideo(media: ProjectSiteDiaryMediaView): boolean {
    return media.isVideoFile;
  }

  protected triggerMediaPreview(media: ProjectSiteDiaryMediaView, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    document
      .querySelector<HTMLAnchorElement>(
        `[data-fancybox="${this.mediaGalleryId}"][data-site-diary-media-id="${media.id}"]`
      )
      ?.click();
  }

  protected isMediaDeleting(media: ProjectSiteDiaryMediaView): boolean {
    return this.deletingMediaIds.has(media.id);
  }

  protected confirmDeleteMedia(media: ProjectSiteDiaryMediaView, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isMediaDeleting(media)) return;

    new RemoveModalController()
      .remove(() => this.deleteMedia(media), undefined, {
        title: 'ProjectsDashboard.siteDiaryEntrySidenav.deleteMediaTitle',
        body: 'ProjectsDashboard.siteDiaryEntrySidenav.deleteMediaDescription',
        submitButtonText: 'ProjectsDashboard.siteDiaryEntrySidenav.deleteMediaConfirm'
      })
      .subscribe();
  }

  protected get mediaGalleryId(): string {
    return `project-site-diary-entry-${this.isControlDocumentation ? this.controlTaskId : this.entryId}`;
  }

  protected openMediaPicker(input: HTMLInputElement): void {
    if (this.uploadingMedia) return;
    input.click();
  }

  protected openAttachmentLibrary(): void {
    if (!this.isControlDocumentation || !this.projectId || this.uploadingMedia) return;

    const attachmentsConfig = { photoAttachments: true, fileAttachments: false, maxAttachments: 200 };
    const dialogRef = this.dialog.open(AttachmentLibraryModalComponent, {
      width: '1120px',
      height: 'calc(100vh - 96px)',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '760px',
      autoFocus: false,
      disableClose: true,
      data: {
        projectId: this.projectId,
        attachmentsConfig,
        attachedIds: (this.controlTask?.attachments || []).map((attachment) => attachment.id),
        maxSelectable: attachmentsConfig.maxAttachments
      }
    });

    dialogRef.afterClosed().subscribe((result?: AttachmentLibraryDialogResult) => {
      if (!result?.sourceAttachmentIds?.length || this.uploadingMedia) return;

      this.uploadingMedia = true;
      this.changeDetector.markForCheck();
      this.attachmentsManageService
        .createAttachmentPacketFromSources(this.projectId, result.sourceAttachmentIds)
        .subscribe({
          next: ({ result: packet }) => {
            if (!packet?.id) return;
            this.attachPacketToControl(packet.id);
          },
          error: () => this.finishMediaUploadWithError()
        });
    });
  }

  protected uploadMedia(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []).filter((file) => file.type.startsWith('image/'));
    input.value = '';

    if (!this.isControlDocumentation || !files.length || this.uploadingMedia) return;

    this.uploadingMedia = true;
    this.changeDetector.markForCheck();
    this.attachmentsManageService.createAttachmentPacket(files).subscribe({
      next: ({ result }) => {
        if (!result?.id) return;
        this.attachPacketToControl(result.id);
      },
      error: () => this.finishMediaUploadWithError()
    });
  }

  private attachPacketToControl(packetId: number): void {
    this.service.updateTaskDocumentation(this.controlTaskId, { attachmentPacketIds: [packetId] }).subscribe({
      next: (updatedEntry) => {
        this.entry = updatedEntry;
        this.media = mapProjectSiteDiaryMedia(updatedEntry.media, this.signatureController);
        this.service.entryChanged.emit({ action: 'update', id: updatedEntry.id });
        this.refreshControlTask();
        this.uploadingMedia = false;
        this.changeDetector.markForCheck();
      },
      error: () => this.finishMediaUploadWithError()
    });
  }

  private finishMediaUploadWithError(): void {
    this.uploadingMedia = false;
    this.snackBar.error(this.translate.instant('ProjectsDashboard.siteDiaryEntrySidenav.uploadMediaError'));
    this.changeDetector.markForCheck();
  }

  private loadEntry(): void {
    if (this.isControlDocumentation) {
      this.loadControlDocumentation();
      return;
    }

    if (!this.entryId) return;

    this.loading = true;
    this.entryDeleted = false;
    this.loadFailed = false;
    this.service.getEntry(this.entryId).subscribe({
      next: (entry) => {
        this.entry = entry;
        this.typeConfig = getEntryTypeConfig(entry.type);
        this.typeLabel = this.translate.instant(
          `ProjectsDashboard.siteDiaryPage.entryTypes.${this.typeConfig.translationKey}`
        );
        this.media = mapProjectSiteDiaryMedia(entry.media, this.signatureController);
        this.loading = false;
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.loading = false;
        this.entryDeleted = isItemNotFoundError(error);
        this.loadFailed = !this.entryDeleted;
        this.changeDetector.markForCheck();
      }
    });
  }

  protected retryLoad(): void {
    this.loadEntry();
  }

  private deleteEntry(): void {
    this.deleting = true;
    const entryId = Number(this.entry?.id);
    this.service.deleteEntry(entryId).subscribe({
      next: () => {
        this.service.entryChanged.emit({ action: 'delete', id: entryId });
        if (this.isControlDocumentation) this.refreshControlTask();
        this.sidenav.close();
      },
      error: () => {
        this.deleting = false;
        this.changeDetector.markForCheck();
      }
    });
  }

  private deleteMedia(media: ProjectSiteDiaryMediaView): void {
    this.deletingMediaIds.add(media.id);
    this.changeDetector.markForCheck();

    this.attachmentService.removeAttachment(media.id).subscribe({
      next: () => {
        this.media = this.media.filter((item) => item.id !== media.id);
        this.entry = this.entry
          ? { ...this.entry, media: (this.entry.media || []).filter((item) => item.id !== media.id) }
          : null;
        this.deletingMediaIds.delete(media.id);
        this.service.entryChanged.emit({ action: 'update', id: this.entry?.id || this.entryId });
        if (this.isControlDocumentation) this.refreshControlTask();
        this.snackBar.success(
          this.translate.instant('ProjectsDashboard.siteDiaryEntrySidenav.deleteMediaSuccess')
        );
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.deletingMediaIds.delete(media.id);
        this.snackBar.error(
          this.translate.instant('ProjectsDashboard.siteDiaryEntrySidenav.deleteMediaError')
        );
        this.changeDetector.markForCheck();
      }
    });
  }

  private loadControlDocumentation(): void {
    this.loading = true;
    this.service.getTaskDocumentation(this.controlTaskId).subscribe({
      next: (entry) => {
        this.entry = entry || {
          id: 0,
          type: 'site_update',
          title: this.controlTask?.description || null,
          description: null,
          task: this.controlTask as any,
          media: []
        };
        this.typeConfig = getEntryTypeConfig('site_update');
        this.typeLabel = this.translate.instant('ProjectsDashboard.stageTasks.taskTypes.control');
        const taskAttachments = this.controlTask?.attachments as ProjectSiteDiaryEntry['media'];
        this.media = mapProjectSiteDiaryMedia(taskAttachments || this.entry.media, this.signatureController);
        this.loading = false;
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.changeDetector.markForCheck();
      }
    });
  }

  private refreshControlTask(): void {
    if (!this.isControlDocumentation || !this.controlTask) return;

    this.taskService.getTask(this.controlTaskId).subscribe({
      next: (task) => {
        Object.assign(this.controlTask, task);
        this.media = mapProjectSiteDiaryMedia(
          task.attachments as ProjectSiteDiaryEntry['media'],
          this.signatureController
        );
        this.listTaskService.emitter.emit({
          type: ListTaskEventType.UPDATE_TASK_ATTACHMENTS,
          data: this.controlTask
        });
        this.changeDetector.markForCheck();
      }
    });
  }
}
